import React, { Dispatch } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, IconCell } from '@components/common';

import { useAppDispatch } from '@store/store';

import { viewOnMap } from '@slices/map';

import { formatDate } from '@utils/kpi-utils/date';

import { EAssetAcquiredStatuses } from '@enums/asset';

import { formatTemplateDefault, timezoneUTC } from '@constants/common';
import { routes } from '@constants/routes';
import { acquiredAssetProfilePath } from './constants';

import ActionsColumnCell from './ActionsColumnCell';

const { map } = routes;

interface IGetColumns {
	navigate: NavigateFunction;
	isViewSiteAccess?: boolean;
	setSelectedAssetEndDate: Dispatch<React.SetStateAction<number | null>>;
	openStopRentingAssetModal: () => void;
	setSelectedItemId: Dispatch<React.SetStateAction<string | null>>;
}

export const getColumns = ({
	navigate,
	isViewSiteAccess,
	setSelectedAssetEndDate,
	openStopRentingAssetModal,
	setSelectedItemId,
}: IGetColumns) => ([
	{
		header: 'Asset ID',
		accessorKey: 'assetId',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const assetId = getValue();

			return <AdaptiveTableCell text={assetId} />;
		},
	},
	{
		header: 'Asset Type',
		accessorKey: 'type',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<IconCell
					id={value?.icon?.id}
					text={value?.assetTypeName}
				/>
			);
		},
	},
	{
		header: 'SubType',
		accessorKey: 'subType',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.assetSubTypeName} />;
		},
	},
	{
		header: 'Asset Owner',
		accessorKey: 'assetOwnerName',
		cell: ({ row }: CellContext<any, any>) => <AdaptiveTableCell text={row?.original?.ownerName} />,
	},
	{
		header: 'Address',
		accessorKey: 'assetAddress',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const assetAddress = getValue();

			return <AdaptiveTableCell text={assetAddress} />;
		},
	},
	{
		header: 'Price',
		accessorKey: 'price',
		cell: ({ getValue }: CellContext<any, any>) => {
			const price = getValue();

			return <AdaptiveTableCell text={price ? `£${price}` : ''} />;
		},
	},
	{
		header: 'Start Date',
		accessorKey: 'startRentDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const startRentDate = getValue();
			const date = formatDate(utcToZonedTime(startRentDate, timezoneUTC), formatTemplateDefault);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'End Date',
		accessorKey: 'endRentDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const endRentDate = getValue();
			const date = formatDate(utcToZonedTime(endRentDate, timezoneUTC), formatTemplateDefault);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Actual end date',
		accessorKey: 'actualEndRentDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const actualEndRentDate = getValue();
			const date = actualEndRentDate
				? formatDate(utcToZonedTime(actualEndRentDate, timezoneUTC), formatTemplateDefault)
				: '-';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Telecom Site',
		accessorKey: 'telecomSite',
		enableSorting: false,
		isHidden: !isViewSiteAccess,
		cell: ({ row }: CellContext<any, any>) => {
			const { linkedSites } = row.original;
			const { siteId } = linkedSites?.length && linkedSites[0] || {};

			return <AdaptiveTableCell text={siteId ? 'yes' : 'no'} />;
		},
	},
	{
		header: 'Rental Status',
		accessorKey: 'rentTime',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const rentTime = getValue();

			return <AdaptiveTableCell text={rentTime?.title} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: function Cell({ row }: CellContext<any, any>) {
			const dispatch = useAppDispatch();
			const assetDbId = row?.original?.assetDbId;
			const itemId = row?.original?.id;
			const rentStatus = row?.original?.rentTime?.name;
			const endRentDate = row?.original?.endRentDate;

			const openProfileHandler = () => navigate(`${acquiredAssetProfilePath}/${assetDbId}`);

			const stopRentingAssetHandler = () => {
				setSelectedItemId(itemId);
				setSelectedAssetEndDate(endRentDate);

				openStopRentingAssetModal();
			};

			const handleViewOnMap = () => {
				void dispatch(viewOnMap({ assetId: assetDbId }));

				navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
			};

			return (
				<ActionsColumnCell
					isStopRentAssetDisable={rentStatus !== EAssetAcquiredStatuses.current}
					isViewAssetProfileDisabled={!assetDbId}
					openProfileHandler={openProfileHandler}
					stopRentingAssetHandler={stopRentingAssetHandler}
					viewOnMapHandler={handleViewOnMap}
				/>
			);
		},
	},
]);
